import React, { useEffect, useRef, useState } from "react";
import { Loader2, Search, SearchCheck, Store } from "lucide-react";
import { D2C_OPS_STORES_API_URL } from "../../Shared/constant";
import { Badge, toneOf } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, clickableRow } from "./ui/table";
import Pagination from "./Pagination";
import StateBlock from "./StateBlock";
import StoreDrawer from "./StoreDrawer";
import StorePicker from "./StorePicker";
import useD2cGet from "./useD2cGet";
import { buildQuery, formatDateTime, formatNumber } from "./d2cFormat";
import { enrolledLabel, standingBadge } from "./storeRules";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const VIA_TONE = { Admin: "info", Auto: "neutral", App: "outline" };

// Stores in D2C, with search, and a way to open any store to switch it.
const D2CStores = ({ canEdit, refreshTick }) => {
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [paging, setPaging] = useState({ key: "", page: 1 });
  const [openStoreId, setOpenStoreId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setQ(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // A new search goes back to page 1 without a second request.
  const page = paging.key === q ? paging.page : 1;
  const setPage = (next) => setPaging({ key: q, page: next });

  const list = useD2cGet(D2C_OPS_STORES_API_URL + buildQuery({ q, page, limit: PAGE_SIZE }));
  const items = list.data?.items || [];
  const total = Number(list.data?.total) || 0;

  // The page header's Refresh reloads this list too.
  const reloadRef = useRef(list.reload);
  reloadRef.current = list.reload;
  useEffect(() => {
    if (refreshTick) reloadRef.current();
  }, [refreshTick]);

  const pick = (storeId) => {
    setPickerOpen(false);
    setOpenStoreId(storeId);
  };

  return (
    <Card>
      <CardHeader className="tw-items-center">
        <div>
          <CardTitle className="tw-flex tw-items-center tw-gap-2">
            Stores in D2C <span className="tw-font-normal tw-text-muted-foreground">({total.toLocaleString("en-IN")})</span>
            {list.loading && items.length > 0 && (
              <Loader2 className="tw-size-3.5 tw-animate-spin tw-text-muted-foreground" aria-hidden="true" />
            )}
          </CardTitle>
          <CardDescription>
            Removed stores stay listed. Click a store to see its readiness{canEdit ? " and switch it on or off" : ""}.
          </CardDescription>
        </div>
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <div className="tw-relative">
            <Search className="tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 tw-size-4 -tw-translate-y-1/2 tw-text-muted-foreground" aria-hidden="true" />
            <Input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name or code"
              aria-label="Search stores in D2C"
              className="tw-w-56 tw-pl-8"
            />
          </div>
          <Button variant="outline" onClick={() => setPickerOpen(true)}>
            <SearchCheck aria-hidden="true" /> {canEdit ? "Find or add a store" : "Find a store"}
          </Button>
        </div>
      </CardHeader>

      <StateBlock
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        isEmpty={items.length === 0}
        emptyIcon={Store}
        emptyText={q ? "No store in D2C matches this search." : "No store is in D2C yet."}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Store</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined via</TableHead>
              <TableHead>Accepting</TableHead>
              <TableHead className="tw-text-right">Radius</TableHead>
              <TableHead className="tw-text-right">Users with access</TableHead>
              <TableHead>Joined</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((store) => {
              const badge = standingBadge(store);
              const via = enrolledLabel(store);
              return (
                <TableRow
                  key={store.store_id}
                  role="button"
                  aria-label={`Open store ${store.name || store.store_id}`}
                  tabIndex={0}
                  className={clickableRow}
                  onClick={() => setOpenStoreId(store.store_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenStoreId(store.store_id);
                    }
                  }}
                >
                  <TableCell className="tw-font-medium">{store.name || store.store_id}</TableCell>
                  <TableCell className="tw-font-mono tw-text-xs">{store.franchise_code || "-"}</TableCell>
                  <TableCell>
                    <Badge tone={toneOf(badge.tone)}>{badge.label}</Badge>
                  </TableCell>
                  <TableCell>{via === "-" ? "-" : <Badge tone={VIA_TONE[via] || "neutral"}>{via}</Badge>}</TableCell>
                  <TableCell>{store.accepting_orders == null ? "-" : store.accepting_orders ? "Yes" : "No"}</TableCell>
                  <TableCell className="tw-text-right tw-tabular-nums">
                    {store.serving_radius_km == null ? "-" : `${store.serving_radius_km} km`}
                  </TableCell>
                  <TableCell className="tw-text-right tw-tabular-nums">{formatNumber(store.users_with_access)}</TableCell>
                  <TableCell className="tw-text-muted-foreground">{formatDateTime(store.joined_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Pagination page={page} limit={PAGE_SIZE} total={total} onPage={setPage} />
      </StateBlock>

      <StorePicker show={pickerOpen} onHide={() => setPickerOpen(false)} onPick={pick} />
      <StoreDrawer storeId={openStoreId} canEdit={canEdit} onClose={() => setOpenStoreId(null)} onChanged={list.reload} />
    </Card>
  );
};

export default D2CStores;
